/**
 * EA-1895 — keeps PayPal's "Learn more" (Pay Later) modal usable on top of the theme.
 *
 * PayPal's SDK (loaded by the PayPal Pay Later Messaging app embed) opens its modal as a
 * fixed, max-z-index container appended to <body>. Two things get in its way:
 *
 * 1. Our drawers (cart drawer, Catering Quick View, …) are modal <dialog>s. A modal dialog
 *    sits in the browser's top layer, above any z-index, and makes the rest of the page
 *    inert — so PayPal's modal opened from inside one appears BEHIND it and can't be used.
 *    While PayPal's modal is open, every open modal dialog is re-shown as a non-modal dialog
 *    (out of the top layer, so PayPal's z-index wins) and switched back once it closes.
 *    The `close`/`toggle` events those swaps fire are swallowed, so nothing in the theme
 *    mistakes them for the drawer closing.
 * 2. PayPal doesn't lock page scroll, so on iOS the page scrolls behind its modal. The page
 *    is locked the same way DialogComponent (assets/dialog.js) does it — a fixed <body> —
 *    unless a drawer has already locked it. A fixed <body> alone still lets iOS pan the
 *    page sideways and rubber-band it when a touch-drag on the modal runs past its edge
 *    (the modal moves with it), so <html> is also clipped and its overscroll turned off.
 *
 * PayPal's markup: `<div id="zoid-paypal-credit-modal-…">` is added on the first click and
 * kept; its inner <div> (after a <style>) carries `<id>-show` while open, and the container
 * `<id>-hide` while closed. Nothing here touches PayPal's iframes (moving one reloads it).
 */

const MODAL_SELECTOR = '[id^="zoid-paypal-credit-modal"]';
const SWAP_EVENTS = ['close', 'toggle', 'beforetoggle'];

/** @type {Set<HTMLDialogElement>} Dialogs demoted to non-modal while PayPal's modal is open. */
const demoted = new Set();
/** @type {Set<HTMLDialogElement>} Dialogs whose swap events are being swallowed. */
const swapping = new Set();

let isOpen = false;
/** @type {number | null} Scroll position to restore, when this script locked the page. */
let lockedScrollY = null;

// Capture on window runs before any listener on the dialog itself.
for (const type of SWAP_EVENTS) {
  window.addEventListener(
    type,
    (event) => {
      if (event.target instanceof HTMLDialogElement && swapping.has(event.target)) {
        event.stopImmediatePropagation();
      }
    },
    true
  );
}

/**
 * Re-opens a dialog without firing events the theme would read as it closing.
 * @param {HTMLDialogElement} dialog
 * @param {boolean} modal
 */
function reopen(dialog, modal) {
  swapping.add(dialog);
  dialog.close();
  if (modal) dialog.showModal();
  else dialog.show();
  // `close` and `toggle` are dispatched from a queued task; stop swallowing after it.
  setTimeout(() => swapping.delete(dialog), 50);
}

/** @param {Element} modal */
function isModalOpen(modal) {
  return !modal.classList.contains(`${modal.id}-hide`) && !!modal.querySelector(`:scope > .${CSS.escape(modal.id)}-show`);
}

/** @type {{ overflow: string, overscrollBehavior: string } | null} <html> styles to restore. */
let rootStyles = null;

function lockScroll() {
  const root = document.documentElement.style;
  rootStyles = { overflow: root.overflow, overscrollBehavior: root.overscrollBehavior };
  root.overflow = 'hidden';
  root.overscrollBehavior = 'none';

  const { style } = document.body;
  if (style.position === 'fixed') return; // A drawer has already locked the page.
  lockedScrollY = window.scrollY;
  style.width = '100%';
  style.position = 'fixed';
  style.top = `-${lockedScrollY}px`;
}

function unlockScroll() {
  if (rootStyles) {
    const root = document.documentElement.style;
    root.overflow = rootStyles.overflow;
    root.overscrollBehavior = rootStyles.overscrollBehavior;
    rootStyles = null;
  }

  if (lockedScrollY === null) return;
  const { style } = document.body;
  style.width = '';
  style.position = '';
  style.top = '';
  window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
  lockedScrollY = null;
}

function onOpen() {
  lockScroll();
  for (const dialog of document.querySelectorAll('dialog[open]')) {
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches(':modal')) continue;
    demoted.add(dialog);
    reopen(dialog, false);
  }
  // Keep keyboard focus in PayPal's modal (re-showing a dialog moves focus into it).
  const frame = /** @type {HTMLIFrameElement | null} */ (document.querySelector(`${MODAL_SELECTOR} iframe`));
  frame?.focus({ preventScroll: true });
}

function onClose() {
  const active = document.activeElement;
  for (const dialog of demoted) {
    if (dialog.open) reopen(dialog, true);
  }
  demoted.clear();
  // Re-showing a modal dialog focuses its first control; put focus back where it was
  // (the message's iframe) when that's still inside an open dialog.
  if (active instanceof HTMLElement && active.closest('dialog[open]')) active.focus({ preventScroll: true });
  unlockScroll();
}

function sync() {
  const open = [...document.querySelectorAll(MODAL_SELECTOR)].some(isModalOpen);
  if (open === isOpen) return;
  isOpen = open;
  if (open) onOpen();
  else onClose();
}

// Watch only PayPal's modal containers for their show/hide classes — not the whole page.
const modalObserver = new MutationObserver(sync);
/** @type {WeakSet<Element>} */
const watched = new WeakSet();

function watchModals() {
  for (const modal of document.querySelectorAll(`body > ${MODAL_SELECTOR}`)) {
    if (watched.has(modal)) continue;
    watched.add(modal);
    modalObserver.observe(modal, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  }
  sync();
}

// PayPal appends its container to <body> on the first "Learn more" click.
new MutationObserver(watchModals).observe(document.body, { childList: true });
watchModals();
